import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Briefcase } from "@phosphor-icons/react";

const F = "w-full h-10 px-3 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:border-[#22D3EE]/50 outline-none";
const L = "block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5";

export default function InvestorProfileForm() {
    const [p, setP] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/account/investor-profile").then(({ data }) => setP(data)).catch(() => {});
    }, []);

    if (!p) return null;
    const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const { configured, ...payload } = p;
            await api.put("/account/investor-profile", payload);
            toast.success("Profilo investitore salvato. Le prossime analisi useranno questi parametri.");
        } catch {
            toast.error("Errore nel salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="border border-gray-200 bg-white p-5 mb-6" data-testid="investor-profile-card">
            <div className="flex items-center gap-2 mb-1">
                <Briefcase size={17} weight="fill" className="text-[#A78BFA]" />
                <div className="font-display-black text-lg">Profilo investitore</div>
                {!p.configured && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700">da confermare</span>
                )}
            </div>
            <p className="text-xs text-gray-600 mb-5">
                Usato dall&apos;Investment Engine per fiscalità, agenzia, target di profitto e Maximum Bid.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className={L}>Soggetto fiscale</label>
                    <select data-testid="ip-entity-type" className={F} value={p.entity_type} onChange={(e) => set("entity_type", e.target.value)}>
                        <option value="da_confermare">Da confermare</option>
                        <option value="persona_fisica">Persona fisica</option>
                        <option value="societa">Società</option>
                    </select>
                </div>
                <div>
                    <label className={L}>Attività di flipping professionale</label>
                    <select data-testid="ip-professional" className={F} value={p.professional_flipper == null ? "nd" : String(p.professional_flipper)} onChange={(e) => set("professional_flipper", e.target.value === "nd" ? null : e.target.value === "true")}>
                        <option value="nd">Da determinare</option>
                        <option value="false">No (occasionale)</option>
                        <option value="true">Sì (abituale)</option>
                    </select>
                </div>
                <div className="flex items-center gap-6 sm:col-span-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" data-testid="ip-prima-casa" checked={!!p.prima_casa} onChange={(e) => set("prima_casa", e.target.checked)} className="accent-[#8B5CF6] w-4 h-4" />
                        Acquisto prima casa
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" data-testid="ip-financing" checked={!!p.financing} onChange={(e) => set("financing", e.target.checked)} className="accent-[#8B5CF6] w-4 h-4" />
                        Uso finanziamento
                    </label>
                </div>
                <div>
                    <label className={L}>Commissione agenzia rivendita (%)</label>
                    <input data-testid="ip-agency-pct" type="number" step="0.5" min="0" max="10" className={F} value={p.agency_pct ?? 3} onChange={(e) => set("agency_pct", parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                    <label className={L}>Capitale proprio disponibile (€)</label>
                    <input data-testid="ip-equity" type="number" step="1000" className={F} value={p.equity_available ?? ""} placeholder="—" onChange={(e) => set("equity_available", e.target.value === "" ? null : parseFloat(e.target.value))} />
                </div>
                <div>
                    <label className={L}>Profitto minimo target (€)</label>
                    <input data-testid="ip-target-profit" type="number" step="1000" className={F} value={p.target_min_profit ?? 20000} onChange={(e) => set("target_min_profit", parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                    <label className={L}>ROI minimo target (%)</label>
                    <input data-testid="ip-target-roi" type="number" step="1" className={F} value={p.target_roi_pct ?? 15} onChange={(e) => set("target_roi_pct", parseFloat(e.target.value) || 0)} />
                </div>
            </div>
            <button
                onClick={save}
                disabled={saving}
                data-testid="ip-save-btn"
                className="mt-5 h-10 px-6 rounded-lg bg-gradient-to-r from-[#3B66F5] to-[#7C3AED] text-white text-sm font-bold hover:brightness-110 disabled:opacity-60 transition-all"
            >
                {saving ? "Salvataggio…" : "Salva profilo"}
            </button>
        </div>
    );
}
